import { CalculateFestivalEnv } from '@/domain/impact-calculate-festival/entities/calculate-festival-env.entity';
import {
  EnvSection,
  EnvTopic,
} from '@/domain/impact-env/entities/question.entity';
import { translate } from '@/ui/i18n';
import { ProjectScoresDetailsData } from '@/ui/pages/app/components/project-page/ProjectScoresDetails/ProjectScoresDetails.types';
import {
  formatUnit,
  getEnvSectionName,
  getEnvTopicName,
} from '@/ui/utils/ess-env-names-and-format';

export function prepareProjectFestivalScoreDetailsDataEnv(
  calculateEnv: CalculateFestivalEnv,
  valueKgeCO2ByParticipant: number,
  valueRefKgeCO2ByParticipant: number,
): ProjectScoresDetailsData[] {
  // CO2 case
  const co2Value = valueKgeCO2ByParticipant;
  const co2RefValue = valueRefKgeCO2ByParticipant;
  const detailsCo2Score = calculateEnv.topics.GLOBAL_CO2.sections.CO2.score;
  const detailsCo2MaxScore =
    calculateEnv.topics.GLOBAL_CO2.sections.CO2.maxScore;

  return [
    {
      title: getEnvTopicName(EnvTopic.GLOBAL_CO2),
      value: co2Value,
      maxValue: NaN,
      refValue: co2RefValue,
      labels: {
        value: translate('Projects.kgeCO2-by-participant'),
        refValue: translate('Projects.ref-value-by-participant'),
      },
      infos: {
        value: translate('Projects.infos-score-detail-card.env-co2'),
        refValue: translate('Projects.infos-score-detail-card.ref-value'),
      },
      viewMode: 'bar',
      answerButtonsAreDisabled: true,
      detailsValues: [
        {
          name: translate('Projects.co2-emissions-note'),
          value: detailsCo2Score,
          unit: `/ ${detailsCo2MaxScore}`,
          maxScore: detailsCo2MaxScore,
          topic: '',
          type: 'env',
          section: '',
          infos: translate(
            'Projects.infos-score-detail-card.env-co2-detail-value',
          ),
        },
      ],
    },
    {
      title: getEnvTopicName(EnvTopic.FOOD_BEVERAGES),
      value: calculateEnv.topics.FOOD_BEVERAGES.score,
      maxValue: calculateEnv.topics.FOOD_BEVERAGES.maxScore,
      refValue: NaN,
      viewMode: 'semi-circle',
      detailsValues: [
        {
          name: getEnvSectionName(EnvSection.CATERING),
          value: calculateEnv.topics.FOOD_BEVERAGES.sections.CATERING.score,
          unit: formatUnit(
            calculateEnv.topics.FOOD_BEVERAGES.sections.CATERING.maxScore,
          ),
          maxScore:
            calculateEnv.topics.FOOD_BEVERAGES.sections.CATERING.maxScore,
          topic: 'FOOD_BEVERAGES',
          type: 'env',
          section: 'CATERING',
        },
        {
          name: getEnvSectionName(EnvSection.BEVERAGES),
          value: calculateEnv.topics.FOOD_BEVERAGES.sections.BEVERAGES.score,
          unit: formatUnit(
            calculateEnv.topics.FOOD_BEVERAGES.sections.BEVERAGES.maxScore,
          ),
          maxScore:
            calculateEnv.topics.FOOD_BEVERAGES.sections.BEVERAGES.maxScore,
          topic: 'FOOD_BEVERAGES',
          type: 'env',
          section: 'BEVERAGES',
        },
        {
          name: getEnvSectionName(EnvSection.DISHES),
          value: calculateEnv.topics.FOOD_BEVERAGES.sections.DISHES.score,
          unit: formatUnit(
            calculateEnv.topics.FOOD_BEVERAGES.sections.DISHES.maxScore,
          ),
          maxScore: calculateEnv.topics.FOOD_BEVERAGES.sections.DISHES.maxScore,
          topic: 'FOOD_BEVERAGES',
          type: 'env',
          section: 'DISHES',
        },
      ],
    },
    {
      title: getEnvTopicName(EnvTopic.RESOURCES_POLLUTION),
      value: calculateEnv.topics.RESOURCES_POLLUTION.score,
      maxValue: calculateEnv.topics.RESOURCES_POLLUTION.maxScore,
      refValue: NaN,
      labels: { value: '', refValue: '' },
      infos: { value: '', refValue: '' },
      viewMode: 'semi-circle',
      detailsValues: [
        {
          name: getEnvSectionName(EnvSection.WATER),
          value: calculateEnv.topics.RESOURCES_POLLUTION.sections.WATER.score,
          unit: formatUnit(
            calculateEnv.topics.RESOURCES_POLLUTION.sections.WATER.maxScore,
          ),
          maxScore:
            calculateEnv.topics.RESOURCES_POLLUTION.sections.WATER.maxScore,
          topic: 'RESOURCES_POLLUTION',
          type: 'env',
          section: 'WATER',
        },
        {
          name: getEnvSectionName(EnvSection.ENERGY),
          value: calculateEnv.topics.RESOURCES_POLLUTION.sections.ENERGY.score,
          unit: formatUnit(
            calculateEnv.topics.RESOURCES_POLLUTION.sections.ENERGY.maxScore,
          ),
          maxScore:
            calculateEnv.topics.RESOURCES_POLLUTION.sections.ENERGY.maxScore,
          topic: 'RESOURCES_POLLUTION',
          type: 'env',
          section: 'ENERGY',
        },
        {
          name: getEnvSectionName(EnvSection.WASTE_ECODESIGN),
          value:
            calculateEnv.topics.RESOURCES_POLLUTION.sections.WASTE_ECODESIGN
              .score,
          unit: formatUnit(
            calculateEnv.topics.RESOURCES_POLLUTION.sections.WASTE_ECODESIGN
              .maxScore,
          ),
          maxScore:
            calculateEnv.topics.RESOURCES_POLLUTION.sections.WASTE_ECODESIGN
              .maxScore,
          topic: 'RESOURCES_POLLUTION',
          type: 'env',
          section: 'WASTE_ECODESIGN',
        },
        {
          name: getEnvSectionName(EnvSection.POLLUTION_LOCATION),
          value:
            calculateEnv.topics.RESOURCES_POLLUTION.sections.POLLUTION_LOCATION
              .score,
          unit: formatUnit(
            calculateEnv.topics.RESOURCES_POLLUTION.sections.POLLUTION_LOCATION
              .maxScore,
          ),
          maxScore:
            calculateEnv.topics.RESOURCES_POLLUTION.sections.POLLUTION_LOCATION
              .maxScore,
          topic: 'RESOURCES_POLLUTION',
          type: 'env',
          section: 'POLLUTION_LOCATION',
        },
      ],
    },
    {
      title: getEnvTopicName(EnvTopic.COMMUNICATION),
      value: calculateEnv.topics.COMMUNICATION.score,
      maxValue: calculateEnv.topics.COMMUNICATION.maxScore,
      refValue: NaN,
      labels: { value: '', refValue: '' },
      infos: { value: '', refValue: '' },
      viewMode: 'semi-circle',
      detailsValues: [
        {
          name: getEnvSectionName(EnvSection.PHYSICAL_COMMUNICATION),
          value:
            calculateEnv.topics.COMMUNICATION.sections.PHYSICAL_COMMUNICATION
              .score,
          unit: formatUnit(
            calculateEnv.topics.COMMUNICATION.sections.PHYSICAL_COMMUNICATION
              .maxScore,
          ),
          maxScore:
            calculateEnv.topics.COMMUNICATION.sections.PHYSICAL_COMMUNICATION
              .maxScore,
          topic: 'COMMUNICATION',
          type: 'env',
          section: 'PHYSICAL_COMMUNICATION',
        },
        {
          name: getEnvSectionName(EnvSection.DIGITAL_COMMUNICATION),
          value:
            calculateEnv.topics.COMMUNICATION.sections.DIGITAL_COMMUNICATION
              .score,
          unit: formatUnit(
            calculateEnv.topics.COMMUNICATION.sections.DIGITAL_COMMUNICATION
              .maxScore,
          ),
          maxScore:
            calculateEnv.topics.COMMUNICATION.sections.DIGITAL_COMMUNICATION
              .maxScore,
          topic: 'COMMUNICATION',
          type: 'env',
          section: 'DIGITAL_COMMUNICATION',
        },
      ],
    },
    {
      title: getEnvTopicName(EnvTopic.PUBLIC_ENGAGEMENT),
      value: calculateEnv.topics.PUBLIC_ENGAGEMENT.score,
      maxValue: calculateEnv.topics.PUBLIC_ENGAGEMENT.maxScore,
      refValue: NaN,
      labels: { value: '', refValue: '' },
      infos: { value: '', refValue: '' },
      viewMode: 'semi-circle',
      detailsValues: [
        {
          name: getEnvSectionName(EnvSection.MOBILITY_STRATEGY),
          value:
            calculateEnv.topics.PUBLIC_ENGAGEMENT.sections.MOBILITY_STRATEGY
              .score,
          unit: formatUnit(
            calculateEnv.topics.PUBLIC_ENGAGEMENT.sections.MOBILITY_STRATEGY
              .maxScore,
          ),
          maxScore:
            calculateEnv.topics.PUBLIC_ENGAGEMENT.sections.MOBILITY_STRATEGY
              .maxScore,
          topic: 'PUBLIC_ENGAGEMENT',
          type: 'env',
          section: 'MOBILITY_STRATEGY',
        },
        {
          name: getEnvSectionName(EnvSection.PUBLIC_INFORMATION),
          value:
            calculateEnv.topics.PUBLIC_ENGAGEMENT.sections.PUBLIC_INFORMATION
              .score,
          unit: formatUnit(
            calculateEnv.topics.PUBLIC_ENGAGEMENT.sections.PUBLIC_INFORMATION
              .maxScore,
          ),
          maxScore:
            calculateEnv.topics.PUBLIC_ENGAGEMENT.sections.PUBLIC_INFORMATION
              .maxScore,
          topic: 'PUBLIC_ENGAGEMENT',
          type: 'env',
          section: 'PUBLIC_INFORMATION',
        },
      ],
    },
  ];
}
